import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ReviewItem {
  id?: string;
  name: string;
  location: string;
  content: string;
  rating: number;
}

export interface SubmitReviewInput {
  organization_id: string;
  customer_name: string;
  customer_location: string | null;
  content: string;
  rating: number;
}

/**
 * Approved customer reviews for public display on the Testimonials section.
 * Returns an empty array (not null) so callers can check `.length > 0` to
 * decide whether to replace the DEFAULT_REVIEWS fallback.
 */
export function useCustomerReviews(orgId: string | undefined) {
  return useQuery({
    queryKey: ['customer-reviews', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<ReviewItem[]> => {
      const { data, error } = await supabase
        .from('customer_reviews')
        .select('id, customer_name, customer_location, content, rating')
        .eq('organization_id', orgId!)
        .eq('is_approved', true)
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) throw error;

      return (data ?? []).map((review) => ({
        id: review.id,
        name: review.customer_name,
        location: review.customer_location || 'Customer',
        content: review.content,
        rating: review.rating,
      }));
    },
  });
}

/**
 * Insert a new (unapproved) customer review submitted via the public form.
 * Invalidates the approved-reviews query so any subsequent approval is
 * reflected immediately.
 */
export function useSubmitReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SubmitReviewInput): Promise<void> => {
      const { error } = await supabase.from('customer_reviews').insert({
        organization_id: input.organization_id,
        customer_name: input.customer_name,
        customer_location: input.customer_location,
        content: input.content,
        rating: input.rating,
        source: 'public_form',
        is_approved: false,
      });
      if (error) throw error;
    },
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: ['customer-reviews', input.organization_id] });
    },
  });
}
